import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    analyzeAddress,
    analyzeApn,
    analyzeMerged,
    clearError,
    clearSuccessMessage,
    clearCurrentAnalysis,
    fetchAnalysisHistory,
} from "../../redux/slices/analysisSlice";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import { useNavigate } from "react-router-dom";
import {
    JURISDICTION_GROUPS
} from "../../utils/jurisdictionData";

// Sub-components
import SearchHeader from "./PropertySearch/SearchHeader";
import SearchLimitBanner from "./PropertySearch/SearchLimitBanner";
import LookupMethodSelector from "./PropertySearch/LookupMethodSelector";
import AnalysisModeToggle from "./PropertySearch/AnalysisModeToggle";
import AddressInput from "./PropertySearch/AddressInput";
import ApnInput from "./PropertySearch/ApnInput";
import MergedLotsDisplay from "./PropertySearch/MergedLotsDisplay";
import AdjacentPropertiesMap from "./PropertySearch/AdjacentPropertiesMap";
import ContourSettings from "./PropertySearch/ContourSettings";
import AnalyzeButton from "./PropertySearch/AnalyzeButton";
import StateConfirmModal from "./PropertySearch/StateConfirmModal";

// Utilities and hooks
import { loadGoogleMapsScript } from "./PropertySearch/utils";
import useNeighbourProperties from "./PropertySearch/useNeighbourProperties";

const PropertySearch = ({ onAnalysisComplete, resultsRef }) => {
    const dispatch = useDispatch();
    const { isLoading, error, successMessage, currentAnalysis, totalAnalyses, analysisStep, analysisStepText } = useSelector(
        (state) => state.analysis
    );
    const { canViewContours, searchLimit } = useFeatureAccess();
    const navigate = useNavigate();

    // Derived state for limits
    const usageCount = totalAnalyses;
    const remainingSearches = Math.max(0, searchLimit - usageCount);
    const isLimitReached = usageCount >= searchLimit;
    const showWarning = !isLimitReached && remainingSearches <= 2;

    // Plan tier label for messages
    const { subscription } = useSelector((state) => state.payment);
    const planInterval = subscription?.plan?.interval === 'year' ? 'Yearly' : 'Monthly';
    const planBaseTier = subscription?.plan?.name?.startsWith('Professional')
        ? 'Professional'
        : subscription?.plan?.name?.startsWith('Standard')
            ? 'Standard'
            : 'Free';

    const planTier = planBaseTier === 'Free' ? 'Free' : `${planBaseTier} ${planInterval}`;

    // Fetch history on mount to ensure count is fresh
    useEffect(() => {
        dispatch(fetchAnalysisHistory({ limit: 100, offset: 0 }));
    }, [dispatch]);

    const [address, setAddress] = useState("");
    const [apn, setApn] = useState("");
    const [lookupBy, setLookupBy] = useState("address");
    const [contourInterval, setContourInterval] = useState(2);
    const [contourTotalLength, setContourTotalLength] = useState("");
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [isLoadingAddress, setIsLoadingAddress] = useState(false);
    const [isLoadingApn, setIsLoadingApn] = useState(false);
    const [forceRefresh, setForceRefresh] = useState(false);
    const [showJurisdictionExplorer, setShowJurisdictionExplorer] = useState(true);

    // Neighbour properties (custom hook)
    const {
        neighbourProperties,
        setNeighbourProperties,
        isLoadingNeighbours,
        selectedPlaceLocation,
        setSelectedPlaceLocation,
        hoveredNeighbour,
        setHoveredNeighbour,
        fetchNeighbourProperties,
        fetchNeighbourPropertiesByApn,
        resolveSingleApn,
        hasFetchedNeighbours,
        selectedParcelGeometry,
        resetNeighbours,
    } = useNeighbourProperties();

    const [mergedLots, setMergedLots] = useState([]);
    const [analysisMode, setAnalysisMode] = useState("single");
    const [primaryAddress, setPrimaryAddress] = useState("");
    const [primaryApn, setPrimaryApn] = useState("");
    const [selectedCity, setSelectedCity] = useState("Los Altos Hills");
    const [selectedCounty, setSelectedCounty] = useState("Santa Clara");
    const [pendingSelection, setPendingSelection] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [zoningError, setZoningError] = useState(null);

    const parseZoningError = (errorStr) => {
        if (!errorStr || typeof errorStr !== "string") return null;
        try {
            let cleanStr = errorStr.trim();
            if (cleanStr.startsWith("422:")) {
                cleanStr = cleanStr.substring(4).trim();
            }
            try {
                return JSON.parse(cleanStr);
            } catch (e) {
                // Normalize Python dictionary representation to JSON
                let jsonLike = cleanStr
                    .replace(/'/g, '"')
                    .replace(/None/g, 'null')
                    .replace(/True/g, 'true')
                    .replace(/False/g, 'false');
                return JSON.parse(jsonLike);
            }
        } catch (e) {
            if (errorStr.includes("NON_RESIDENTIAL_PARCEL")) {
                const msgMatch = errorStr.match(/'message':\s*"([^"]+)"/) || errorStr.match(/"message":\s*"([^"]+)"/);
                const zoneMatch = errorStr.match(/'zone_code':\s*'([^']+)'/) || errorStr.match(/"zone_code":\s*"([^"]+)"/);
                const suggestionMatch = errorStr.match(/'suggestion':\s*'([^']+)'/) || errorStr.match(/"suggestion":\s*"([^"]+)"/);
                return {
                    error_type: "NON_RESIDENTIAL_PARCEL",
                    zone_code: zoneMatch ? zoneMatch[1] : "Unknown",
                    message: msgMatch ? msgMatch[1] : "This platform currently analyzes residential properties only.",
                    suggestion: suggestionMatch ? suggestionMatch[1] : "Please search for a residential property.",
                    commercial_analysis: "Commercial and mixed-use analysis is on our roadmap."
                };
            }
        }
        return null;
    };



    const autocompleteServiceRef = useRef();
    const placesServiceRef = useRef();
    const addressInputRef = useRef();

    // Use environment variable for Google Maps API Key
    const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    useEffect(() => {
        if (!GOOGLE_API_KEY) {
            console.error("Google Maps API key is not configured");
            toast.error("Map service configuration error");
            return;
        }

        loadGoogleMapsScript(GOOGLE_API_KEY)
            .then(() => {
                autocompleteServiceRef.current =
                    new window.google.maps.places.AutocompleteService();
                const hiddenDiv = document.createElement("div");
                placesServiceRef.current = new window.google.maps.places.PlacesService(
                    hiddenDiv
                );
            })
            .catch((error) => {
                console.error("Error loading Google Maps:", error);
                toast.error("Failed to load address autocomplete service");
            });
    }, [GOOGLE_API_KEY]);

    // Handle success with smooth scroll
    useEffect(() => {
        if (currentAnalysis && successMessage) {
            toast.success("✅ Analysis completed successfully!", {
                position: "top-right",
                autoClose: 3000,
            });

            if (onAnalysisComplete) {
                onAnalysisComplete(currentAnalysis);
            }

            // Smooth scroll to results after a brief delay to allow rendering
            setTimeout(() => {
                if (resultsRef && resultsRef.current) {
                    resultsRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest'
                    });
                }
            }, 300);

            // Clear form after successful analysis
            setAddress("");
            setApn("");
            setPrimaryAddress("");
            setPrimaryApn("");
            setAddressSuggestions([]);
            setNeighbourProperties([]);
            setSelectedPlaceLocation(null);
            hasFetchedNeighbours.current = false;

            dispatch(clearSuccessMessage());
        }
    }, [currentAnalysis, successMessage, onAnalysisComplete, dispatch, resultsRef, setNeighbourProperties, setSelectedPlaceLocation, hasFetchedNeighbours, setPrimaryAddress, setPrimaryApn]);

    // Handle errors
    useEffect(() => {
        if (error) {
            const parsedZoning = parseZoningError(error);
            if (parsedZoning) {
                setZoningError(parsedZoning);
            } else {
                toast.error(`❌ ${error}`, {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
            dispatch(clearError());
        }
    }, [error, dispatch]);

    // Fetch neighbours automatically if switching to merged/combined lots mode and we have a selected location/APN
    useEffect(() => {
        if (analysisMode === "merged" && neighbourProperties.length === 0 && !isLoadingNeighbours && !hasFetchedNeighbours.current) {
            if (lookupBy === "address" && selectedPlaceLocation && primaryAddress) {
                console.log('🔄 [Neighbours] Mode switched to merged. Fetching neighbours for primary address.');
                fetchNeighbourProperties(selectedPlaceLocation, primaryAddress);
            } else if (lookupBy === "apn" && primaryApn) {
                console.log('🔄 [Neighbours] Mode switched to merged. Fetching neighbours for primary APN.');
                fetchNeighbourPropertiesByApn(primaryApn, selectedCity, selectedCounty);
            }
        }
    }, [analysisMode, lookupBy, selectedPlaceLocation, primaryAddress, primaryApn, selectedCity, selectedCounty, neighbourProperties.length, isLoadingNeighbours, fetchNeighbourProperties, fetchNeighbourPropertiesByApn, hasFetchedNeighbours]);

    const selectedState = React.useMemo(() => {
        const group = JURISDICTION_GROUPS.find(g => g.county === selectedCounty);
        return group ? group.state : "CA";
    }, [selectedCounty]);

    const debouncedSearchRef = useRef(null);

    const handleAddressChange = (e) => {
        const value = e.target.value;
        setAddress(value);

        // Clear neighbours when user types a new address
        resetNeighbours();

        if (debouncedSearchRef.current) {
            clearTimeout(debouncedSearchRef.current);
        }

        if (value.length >= 2 && autocompleteServiceRef.current) {
            setIsLoadingAddress(true);

            debouncedSearchRef.current = setTimeout(() => {
                // Bias bounds dynamically based on active state (California or Texas)
                let bounds = null;
                if (selectedState === "TX") {
                    // Texas (Collin & Denton counties area)
                    const sw = new window.google.maps.LatLng(32.90, -97.20);
                    const ne = new window.google.maps.LatLng(33.45, -96.30);
                    bounds = new window.google.maps.LatLngBounds(sw, ne);
                } else {
                    // California Bay Area (Santa Clara, San Mateo, Alameda, Contra Costa, Marin, Napa, Solano, Calaveras)
                    const sw = new window.google.maps.LatLng(37.00, -122.60);
                    const ne = new window.google.maps.LatLng(38.80, -120.30);
                    bounds = new window.google.maps.LatLngBounds(sw, ne);
                }

                const targetCity = selectedCity || "Los Altos Hills";
                const targetState = selectedState || "CA";

                // Tight LatLngBounds strictly wrapping Los Altos Hills, CA town boundaries
                const losAltosHillsSW = new window.google.maps.LatLng(37.3300, -122.1800);
                const losAltosHillsNE = new window.google.maps.LatLng(37.4000, -122.1000);
                const losAltosHillsBounds = new window.google.maps.LatLngBounds(losAltosHillsSW, losAltosHillsNE);

                const queries = [
                    { input: value, bounds: losAltosHillsBounds, location: losAltosHillsSW, radius: 5000 },
                    { input: `${value}, ${targetCity}`, bounds: losAltosHillsBounds },
                    { input: `${value}, ${targetCity}, ${targetState}` }
                ];

                const promises = queries.map(q => {
                    const req = {
                        input: q.input,
                        types: ["address"],
                        componentRestrictions: { country: "us" },
                        bounds: losAltosHillsBounds,
                        strictBounds: true,
                        ...(q.location ? { location: q.location, radius: q.radius } : {})
                    };
                    return new Promise((resolve) => {
                        autocompleteServiceRef.current.getPlacePredictions(
                            req,
                            (predictions, status) => {
                                if (
                                    status === window.google.maps.places.PlacesServiceStatus.OK &&
                                    predictions
                                ) {
                                    resolve(predictions);
                                } else {
                                    resolve([]);
                                }
                            }
                        );
                    });
                });

                Promise.all(promises).then((resultsFlat) => {
                    setIsLoadingAddress(false);
                    const allPredictions = resultsFlat.flat();
                    
                    // Deduplicate by place_id
                    const seen = new Set();
                    const uniquePredictions = [];
                    for (const pred of allPredictions) {
                        if (!seen.has(pred.place_id)) {
                            seen.add(pred.place_id);
                            uniquePredictions.push(pred);
                        }
                    }

                    const targetCityLower = targetCity.toLowerCase();

                    // Strictly filter ONLY for Los Altos Hills properties (exclude other cities completely)
                    const losAltosHillsOnly = uniquePredictions.filter(prediction => {
                        const description = (prediction.description || "").toLowerCase();
                        const secondaryText = (prediction.structured_formatting?.secondary_text || "").toLowerCase();
                        return (description.includes(targetCityLower) || secondaryText.includes(targetCityLower))
                            && !description.includes("pathway"); // exclude 'Los Altos Hills Pathway' street name artifact
                    });

                    console.log('🔍 [Autocomplete] Query:', value, '| Target City:', targetCity, '| Displaying Los Altos Hills predictions:', losAltosHillsOnly.map(p => p.description));

                    setAddressSuggestions(losAltosHillsOnly);
                }).catch((err) => {
                    console.error("❌ [Autocomplete] Error fetching multi-city autocomplete predictions:", err);
                    setIsLoadingAddress(false);
                });
            }, 300); // 300ms debounce
        } else {
            setIsLoadingAddress(false);
            setAddressSuggestions([]);
        }
    };

    const handleNeighbourClick = (neighbour) => {
        if (analysisMode === "merged") {
            // In merged mode, clicking a card acts as a shortcut to toggle merging
            handleToggleMerge(neighbour);
            return;
        }

        if (lookupBy === "apn") {
            if (neighbour.apn) {
                setApn(neighbour.apn);
                setPrimaryApn(neighbour.apn);
                setNeighbourProperties([]);
                if (neighbour.lat && neighbour.lng) {
                    setSelectedPlaceLocation(new window.google.maps.LatLng(neighbour.lat, neighbour.lng));
                }
            }
        } else {
            // Auto-fill the search box with the neighbour's address
            const fullAddress = neighbour.address || neighbour.name;
            setAddress(fullAddress);
            setPrimaryAddress(fullAddress);
            setMergedLots([]);
            setNeighbourProperties([]);
            setSelectedPlaceLocation(null);
            hasFetchedNeighbours.current = false;

            // If the neighbour has a placeId, get full formatted address
            if (neighbour.placeId && placesServiceRef.current) {
                placesServiceRef.current.getDetails(
                    { placeId: neighbour.placeId },
                    (place, status) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                            setAddress(place.formatted_address);
                            setPrimaryAddress(place.formatted_address);
                        }
                    }
                );
            }
        }
    };

    const handleToggleMerge = (neighbour, e) => {
        if (e) {
            e.stopPropagation(); // Avoid triggering card click
        }
        setMergedLots(prev => {
            const exists = prev.some(l => (neighbour.apn && l.apn) ? l.apn === neighbour.apn : l.address === neighbour.address);
            if (exists) {
                return prev.filter(l => (neighbour.apn && l.apn) ? l.apn !== neighbour.apn : l.address !== neighbour.address);
            } else {
                return [...prev, neighbour];
            }
        });
    };

    const handleAddressSelect = (prediction) => {
        console.log('📍 [Address] Selected:', prediction.description);
        setAddressSuggestions([]);
        setIsLoadingAddress(true);

        if (placesServiceRef.current) {
            placesServiceRef.current.getDetails(
                { placeId: prediction.place_id, fields: ['formatted_address', 'geometry', 'name', 'address_components'] },
                (place, status) => {
                    setIsLoadingAddress(false);
                    if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                        console.log('📍 [Address] Resolved:', place.formatted_address);
                        console.log('📍 [Address] Location:', place.geometry?.location?.lat(), place.geometry?.location?.lng());

                        // Extract city and state to check for target mismatch
                        const addressComponents = place.address_components || [];
                        const localityComp = addressComponents.find(c => c.types.includes("locality"));
                        const stateComp = addressComponents.find(c => c.types.includes("administrative_area_level_1"));
                        
                        let parsedCity = localityComp ? localityComp.long_name : "";
                        let parsedState = stateComp ? stateComp.short_name : "";

                        if (!parsedCity || !parsedState) {
                            const parts = place.formatted_address.split(',');
                            if (parts.length >= 3) {
                                const stateZip = parts[parts.length - 2].trim().split(' ');
                                parsedState = stateZip[0].trim();
                                parsedCity = parts[parts.length - 3].trim();
                            }
                        }

                        const matchedGroup = JURISDICTION_GROUPS.find(group => {
                            const stateMatches = group.state.toLowerCase() === parsedState.toLowerCase();
                            if (!stateMatches) return false;
                            return group.cities.some(c => c.toLowerCase() === parsedCity.toLowerCase());
                        });

                        const executeSelection = (finalCity, finalCounty, finalState) => {
                            if (finalCity && finalCounty) {
                                setSelectedCity(finalCity);
                                setSelectedCounty(finalCounty);
                                toast.info(`📍 Switched target jurisdiction to ${finalCity}, ${finalState}.`);
                            }

                            if (analysisMode === "merged") {
                                if (!primaryAddress) {
                                    // First one selected in merged mode becomes the primary
                                    resetNeighbours();
                                    setPrimaryAddress(place.formatted_address);
                                    setAddress(""); // Clear input to type next one
                                    if (place.geometry && place.geometry.location) {
                                        fetchNeighbourProperties(place.geometry.location, place.formatted_address, prediction.place_id);
                                    }
                                } else {
                                    // Already have primary, add to mergedLots list
                                    const newLot = {
                                        address: place.formatted_address,
                                        name: place.name || place.formatted_address.split(',')[0],
                                        placeId: prediction.place_id
                                    };
                                    setMergedLots(prev => {
                                        if (prev.some(l => l.address === newLot.address) || newLot.address === primaryAddress) {
                                            return prev;
                                        }
                                        return [...prev, newLot];
                                    });
                                    setAddress(""); // Clear input to type next one
                                }
                            } else {
                                // Single Mode
                                resetNeighbours();
                                setAddress(place.formatted_address);
                                setPrimaryAddress(place.formatted_address);
                                setMergedLots([]);

                                if (place.geometry && place.geometry.location) {
                                    setSelectedPlaceLocation(place.geometry.location);
                                }
                            }
                        };

                        if (matchedGroup) {
                            const matchedCityName = matchedGroup.cities.find(c => c.toLowerCase() === parsedCity.toLowerCase());
                            const isMismatch = (matchedCityName && matchedCityName !== selectedCity) || (matchedGroup.state !== selectedState);

                            if (isMismatch) {
                                setPendingSelection({
                                    place,
                                    prediction,
                                    matchedCity: matchedCityName,
                                    matchedCounty: matchedGroup.county,
                                    matchedState: matchedGroup.state,
                                    execute: () => executeSelection(matchedCityName, matchedGroup.county, matchedGroup.state)
                                });
                                setIsConfirmModalOpen(true);
                                return;
                            }
                        }

                        // No mismatch or not supported city, proceed directly
                        executeSelection();
                    }
                }
            );
        }
    };

    const handleApnSubmit = async (value) => {
        if (!value) return;
        setIsLoadingApn(true);
        try {
            if (analysisMode === "merged") {
                if (!primaryApn) {
                    // Set primary APN
                    resetNeighbours();
                    const res = await fetchNeighbourPropertiesByApn(value, selectedCity, selectedCounty);
                    if (res) {
                        setPrimaryApn(res.apn);
                        setApn("");
                    } else {
                        toast.error(`Could not locate parcel boundary for APN: ${value}`);
                    }
                } else {
                    // Already have primary APN, add this as a merged lot
                    const res = await resolveSingleApn(value, selectedCity, selectedCounty);
                    if (res) {
                        const newLot = {
                            apn: res.apn,
                            address: res.address,
                            name: `APN: ${res.apn}`,
                            location: res.location,
                            parcelRings: res.geometry?.rings
                        };
                        setMergedLots(prev => {
                            if (prev.some(l => l.apn === newLot.apn) || newLot.apn === primaryApn) {
                                return prev;
                            }
                            return [...prev, newLot];
                        });
                        setApn("");
                    } else {
                        toast.error(`Could not locate parcel boundary for APN: ${value}`);
                    }
                }
            } else {
                // Single Mode
                const res = await resolveSingleApn(value, selectedCity, selectedCounty);
                if (res) {
                    resetNeighbours();
                    setApn(res.apn);
                    setPrimaryApn(res.apn);
                    setMergedLots([]);
                    if (res.location) {
                        setSelectedPlaceLocation(res.location);
                    }
                } else {
                    toast.error(`Could not find APN: ${value}`);
                }
            }
        } catch (e) {
            console.error("APN submission error:", e);
            toast.error("Failed to query APN. Please check the number and city/county selection.");
        } finally {
            setIsLoadingApn(false);
        }
    };

    const handleAnalyze = async () => {
        if (!hasSelection) {
            toast.warning("⚠️ Please enter an address or APN", {
                position: "top-right",
            });
            return;
        }

        if (isLimitReached) {
            toast.info(
                <div className="flex flex-col gap-2">
                    <span className="font-bold">{planTier} Search Limit Reached</span>
                    {planBaseTier === 'Professional' ? (
                        <span>You've used all {searchLimit} monthly searches included in your {planTier} plan. Contact support for enterprise options.</span>
                    ) : (
                        <>
                            <span>You've used all {searchLimit} searches allowed this month for your {planTier.toLowerCase()} plan. Upgrade to increase your monthly allowance.</span>
                            <button
                                onClick={() => navigate('/#subscription-plans')}
                                className="mt-2 bg-white text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold self-start hover:bg-blue-50"
                            >
                                View Plans
                            </button>
                        </>
                    )}
                </div>,
                { autoClose: 5000 }
            );
            return;
        }

        dispatch(clearError());
        dispatch(clearCurrentAnalysis());

        try {
            if (lookupBy === "address") {
                const targetPrimary = analysisMode === "merged" ? primaryAddress : (address || primaryAddress);
                if (analysisMode === "merged" && mergedLots.length > 0) {
                    if (!targetPrimary) {
                        toast.warning("⚠️ Please select a primary property first", { position: "top-right" });
                        return;
                    }
                    await dispatch(
                        analyzeMerged({
                            addresses: [targetPrimary, ...mergedLots.map(l => l.address)],
                            contourInterval,
                            forceRefresh: forceRefresh,
                        })
                    );
                } else {
                    if (!targetPrimary) {
                        toast.warning("⚠️ Please enter a property address", { position: "top-right" });
                        return;
                    }
                    await dispatch(
                        analyzeAddress({
                            address: targetPrimary,
                            contourInterval,
                            contourTotalLength: contourTotalLength ? parseFloat(contourTotalLength) : null,
                            skipDem: true,
                            forceRefresh: forceRefresh,
                        })
                    );
                }
            } else {
                const targetPrimary = analysisMode === "merged" ? primaryApn : (apn || primaryApn);
                if (analysisMode === "merged" && mergedLots.length > 0) {
                    if (!targetPrimary) {
                        toast.warning("⚠️ Please select a primary APN first", { position: "top-right" });
                        return;
                    }
                    await dispatch(
                        analyzeMerged({
                            apns: [targetPrimary, ...mergedLots.map(l => l.apn || l.address)],
                            city: selectedCity,
                            contourInterval,
                            forceRefresh: forceRefresh,
                        })
                    );
                } else {
                    if (!targetPrimary) {
                        toast.warning("⚠️ Please enter a property APN", { position: "top-right" });
                        return;
                    }
                    await dispatch(
                        analyzeApn({
                            apn: targetPrimary,
                            city: selectedCity,
                            contourInterval,
                            contourTotalLength: contourTotalLength ? parseFloat(contourTotalLength) : null,
                            skipDem: true,
                            forceRefresh: forceRefresh,
                        })
                    );
                }
            }
        } finally {
        }
    };

    const hasSelection = lookupBy === "apn"
        ? (analysisMode === "merged" ? !!primaryApn : (!!apn || !!primaryApn))
        : (analysisMode === "merged" ? !!primaryAddress : !!address);

    return (
        <>
            <div className="max-w-7xl ">
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200">
                    <SearchHeader
                        showJurisdictionExplorer={showJurisdictionExplorer}
                        setShowJurisdictionExplorer={setShowJurisdictionExplorer}
                        setAddress={setAddress}
                        setApn={setApn}
                        setLookupBy={setLookupBy}
                        selectedState={selectedState}
                        selectedCity={selectedCity}
                        onStateSwitch={(stateCode) => {
                            // Find the first county/city in the target state
                            const firstGroup = JURISDICTION_GROUPS.find(g => g.state === stateCode);
                            if (firstGroup) {
                                const firstCity = firstGroup.cities[0];
                                setSelectedCity(firstCity);
                                setSelectedCounty(firstGroup.county);
                                setAddress(`, ${firstCity}, ${stateCode}`);
                                setApn("");
                                setLookupBy("address");
                                toast.info(`📍 Switched to ${stateCode === "TX" ? "Texas" : "California"}. Targeting ${firstCity}, ${stateCode}.`);
                                setShowJurisdictionExplorer(true);
                                setTimeout(() => {
                                    if (addressInputRef.current) {
                                        addressInputRef.current.focus();
                                        addressInputRef.current.setSelectionRange(0, 0);
                                    }
                                }, 150);
                            }
                        }}
                        onSelectCity={(city, county) => {
                            setSelectedCity(city);
                            setSelectedCounty(county);
                            const group = JURISDICTION_GROUPS.find(g => g.county === county);
                            const stateCode = group?.state || "CA";
                            setAddress(`, ${city}, ${stateCode}`);
                            setApn("");
                            setLookupBy("address");
                            toast.info(`📍 Targeting ${city}, ${stateCode}. Start typing your street address.`);
                            setTimeout(() => {
                                if (addressInputRef.current) {
                                    addressInputRef.current.focus();
                                    addressInputRef.current.setSelectionRange(0, 0);
                                }
                            }, 150);
                        }}
                    />

                    <SearchLimitBanner
                        showWarning={showWarning}
                        isLimitReached={isLimitReached}
                        planTier={planTier}
                        planBaseTier={planBaseTier}
                        searchLimit={searchLimit}
                        remainingSearches={remainingSearches}
                    />

                    <div className="p-4 sm:p-6 space-y-4">
                        <LookupMethodSelector
                            lookupBy={lookupBy}
                            setLookupBy={setLookupBy}
                            isLoading={isLoading}
                        />

                        {/* Analysis Mode (Single / Combined) Toggle */}
                        <AnalysisModeToggle
                            analysisMode={analysisMode}
                            setAnalysisMode={setAnalysisMode}
                            isLoading={isLoading}
                            address={lookupBy === "address" ? address : apn}
                            primaryAddress={lookupBy === "address" ? primaryAddress : primaryApn}
                            setAddress={lookupBy === "address" ? setAddress : setApn}
                            setPrimaryAddress={lookupBy === "address" ? setPrimaryAddress : setPrimaryApn}
                            setMergedLots={setMergedLots}
                            setNeighbourProperties={setNeighbourProperties}
                        />

                        {/* Input Field */}
                        {lookupBy === "address" ? (
                            <>
                                <AddressInput
                                    addressInputRef={addressInputRef}
                                    address={address}
                                    onAddressChange={handleAddressChange}
                                    isLoading={isLoading}
                                    isLimitReached={isLimitReached}
                                    isLoadingAddress={isLoadingAddress}
                                    addressSuggestions={addressSuggestions}
                                    onAddressSelect={handleAddressSelect}
                                    analysisMode={analysisMode}
                                    primaryAddress={primaryAddress}
                                    planTier={planTier}
                                    searchLimit={searchLimit}
                                    selectedCity={selectedCity}
                                    selectedState={selectedState}
                                />

                                {/* Merged Lots Display */}
                                {analysisMode === "merged" && primaryAddress && (
                                    <MergedLotsDisplay
                                        primaryAddress={primaryAddress}
                                        mergedLots={mergedLots}
                                        setPrimaryAddress={setPrimaryAddress}
                                        setMergedLots={setMergedLots}
                                        setAddress={setAddress}
                                        lookupBy={lookupBy}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <ApnInput
                                    apn={apn}
                                    onApnChange={setApn}
                                    onApnSubmit={handleApnSubmit}
                                    isLoading={isLoading}
                                    isLimitReached={isLimitReached}
                                    isLoadingApn={isLoadingApn}
                                    analysisMode={analysisMode}
                                    primaryApn={primaryApn}
                                    planTier={planTier}
                                    searchLimit={searchLimit}
                                />

                                {/* Merged Lots Display */}
                                {analysisMode === "merged" && primaryApn && (
                                    <MergedLotsDisplay
                                        primaryAddress={primaryApn}
                                        mergedLots={mergedLots}
                                        setPrimaryAddress={setPrimaryApn}
                                        setMergedLots={setMergedLots}
                                        setAddress={setApn}
                                        lookupBy={lookupBy}
                                    />
                                )}
                            </>
                        )}

                        {/* Neighbouring Properties Section */}
                        <AdjacentPropertiesMap
                            analysisMode={analysisMode}
                            isLoadingNeighbours={isLoadingNeighbours}
                            neighbourProperties={neighbourProperties}
                            lookupBy={lookupBy}
                            selectedPlaceLocation={selectedPlaceLocation}
                            selectedParcelGeometry={selectedParcelGeometry}
                            mergedLots={mergedLots}
                            hoveredNeighbour={hoveredNeighbour}
                            setHoveredNeighbour={setHoveredNeighbour}
                            onNeighbourClick={handleNeighbourClick}
                            onToggleMerge={handleToggleMerge}
                            primaryAddress={lookupBy === "address" ? primaryAddress : primaryApn}
                        />

                        <ContourSettings
                            canViewContours={canViewContours}
                            contourInterval={contourInterval}
                            setContourInterval={setContourInterval}
                            contourTotalLength={contourTotalLength}
                            setContourTotalLength={setContourTotalLength}
                            isLoading={isLoading}
                            forceRefresh={forceRefresh}
                            setForceRefresh={setForceRefresh}
                        />

                        <AnalyzeButton
                            isLoading={isLoading}
                            isLimitReached={isLimitReached}
                            hasSelection={hasSelection}
                            searchLimit={searchLimit}
                            mergedLots={mergedLots}
                            analysisStep={analysisStep}
                            analysisStepText={analysisStepText}
                            selectedState={selectedState}
                            onAnalyze={handleAnalyze}
                        />
                    </div>
                </div>
            </div>

            <StateConfirmModal
                isOpen={isConfirmModalOpen}
                selectedAddress={pendingSelection?.place?.formatted_address || pendingSelection?.prediction?.description || ""}
                targetCity={pendingSelection?.matchedCity || ""}
                targetState={pendingSelection?.matchedState || ""}
                currentCity={selectedCity}
                currentState={selectedState}
                onConfirm={() => {
                    if (pendingSelection?.execute) {
                        pendingSelection.execute();
                    }
                    setIsConfirmModalOpen(false);
                    setPendingSelection(null);
                }}
                onCancel={() => {
                    setIsConfirmModalOpen(false);
                    setPendingSelection(null);
                    setAddress(""); // Reset/clear search input since they cancelled
                }}
            />

            {zoningError && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-[2rem] shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden animate-slide-up">
                        {/* Header Banner */}
                        <div className="bg-gradient-to-r from-amber-500 to-red-600 px-8 py-6 text-white relative">
                            <div className="absolute top-4 right-4 text-white/40 hover:text-white cursor-pointer transition-colors" onClick={() => setZoningError(null)}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/10 rounded-xl backdrop-blur-md">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-amber-200 animate-pulse">Zoning Restriction</span>
                                    <h3 className="text-xl font-black tracking-tight text-white">Non-Residential Parcel</h3>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-8 space-y-6">
                            <div className="space-y-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Error Details</span>
                                <p className="text-slate-600 text-sm leading-relaxed font-medium">
                                    {zoningError.message}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-1">Zoning Designation</span>
                                    <span className="text-md font-extrabold text-slate-800">{zoningError.zone_code || "INST"}</span>
                                </div>
                                <div className="p-4 bg-indigo-50/50 border border-indigo-100/50 rounded-2xl">
                                    <span className="text-[9px] font-black uppercase tracking-widest text-indigo-400 block mb-1">Status</span>
                                    <span className="text-md font-extrabold text-indigo-600">Blocked</span>
                                </div>
                            </div>

                            <div className="p-4 bg-amber-50/60 border border-amber-100/60 rounded-2xl space-y-1">
                                <span className="text-[9px] font-black uppercase tracking-widest text-amber-600 block">Next Steps / Suggestion</span>
                                <p className="text-amber-800 text-xs leading-relaxed font-medium">
                                    {zoningError.suggestion}
                                </p>
                            </div>

                            <div className="border-t border-slate-100 pt-6 flex flex-col sm:flex-row gap-3 justify-between items-center">
                                <span className="text-slate-400 text-[10px] font-medium text-center sm:text-left leading-normal">
                                    {zoningError.commercial_analysis}
                                </span>
                                <button 
                                    onClick={() => setZoningError(null)}
                                    className="w-full sm:w-auto px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-[13px] transition-all active:scale-[0.98]"
                                >
                                    Acknowledge
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PropertySearch;